import { action, makeObservable, observable } from 'mobx';
import { ProgramDateTypes } from '../../models/program-date-type';
import { blankProgramDate, ProgramDate, ProgramDateTypeCode, ProgramDateTypeCodes } from '../../models';
import { parseISO } from 'date-fns';
import { getISO8601PacificDateFromDateTime } from '../../common/date/get-iso-8601-pacific-date';

type ProgramDateDateField = 'startDate' | 'endDate' | 'displayStartDate' | 'displayEndDate';

const windowNameMapping: Record<ProgramDateTypeCode, string> = {
  'Nomination-Window': 'Nomination window',
  'Scrubbing-Window': 'Nomination Evaluation window',
  'Judging-Window': 'Judging window',
  'Review-&-Designate-Window': 'Review window',
  'Compliance-&-Revisions-Window': 'Compliance window',
  'Winners-Window': 'Winners window'
};

const allowedOverlaps: [ProgramDateTypeCode, ProgramDateTypeCode][] = [
  ['Nomination-Window', 'Scrubbing-Window']
];

export class ProgramDatesPageUiStore {
  @observable originalProgramDates: ProgramDate[] = [];

  @observable editingProgramDates: ProgramDate[] = [];
  
  @observable validationMessages: Map<ProgramDateTypeCode, string> = new Map<ProgramDateTypeCode, string>();

  constructor() {
    makeObservable(this);
  }

  private canWindowsOverlap(window1: ProgramDateTypeCode, window2: ProgramDateTypeCode): boolean {
    return allowedOverlaps.some(([w1, w2]) => 
      (w1 === window1 && w2 === window2) || (w1 === window2 && w2 === window1)
    );
  };

  private getWindowName(dateTypeCode: ProgramDateTypeCode): string {
    return windowNameMapping[dateTypeCode];
  };

  private doDateRangesOverlap(
    start1: string, 
    end1: string, 
    start2: string, 
    end2: string
  ): boolean {
    const startDate1 = parseISO(start1);
    const endDate1 = parseISO(end1);
    const startDate2 = parseISO(start2);
    const endDate2 = parseISO(end2);

    return startDate1 <= endDate2 && startDate2 <= endDate1;
  }

  @action
  getProgramDate(dateTypeCode: ProgramDateTypeCode): ProgramDate | undefined {
    return this.editingProgramDates.find(programDate => programDate.dateTypeCode === dateTypeCode);
  };

  @action
  resetStates(): void {
    this.originalProgramDates = [];
    this.editingProgramDates = [];
    this.validationMessages.clear();
  }

  @action
  setProgramDates(programId: number, programDates: ProgramDate[]): void {
    const existingProgramDates = programDates.filter(date => date.programDateId > 0);
    const existingDateTypes = new Set(existingProgramDates.map(date => date.dateTypeCode));
    
    const newProgramDates = ProgramDateTypeCodes
      .filter(code => !existingDateTypes.has(code))
      .map(code => blankProgramDate(programId, code));
    
    this.originalProgramDates = programDates;
    this.editingProgramDates = [...existingProgramDates, ...newProgramDates];
  }

  @action
  updateProgramDate(
    dateTypeCode: ProgramDateTypeCode,
    field: ProgramDateDateField,
    value: Date | null,
  ): void {
    const editingProgramDate = this.editingProgramDates.find(date => date.dateTypeCode === dateTypeCode);
    if (!editingProgramDate) return;

    if (!value) {
      editingProgramDate[field] = '';

      if (field === 'startDate') {
        editingProgramDate.displayStartDate = '';
      } else if (field === 'endDate') {
        editingProgramDate.displayEndDate = '';
      }

      return;
    }

    const pacificDate = getISO8601PacificDateFromDateTime(value);

    if (field === 'startDate') {
      editingProgramDate.startDate = pacificDate;
      editingProgramDate.displayStartDate = pacificDate;
    } 
    
    if (field === 'endDate') {
      editingProgramDate.endDate = pacificDate;
      editingProgramDate.displayEndDate = pacificDate;
    }

    if (field === 'displayStartDate') {
      editingProgramDate.displayStartDate = pacificDate;
    }

    if (field === 'displayEndDate') {
      editingProgramDate.displayEndDate = pacificDate;
    }
  }

  private validateProgramDate(programDate: ProgramDate): boolean {
    const {
      dateTypeCode,
      startDate,
      endDate,
      displayStartDate,
      displayEndDate,
    } = programDate;
    const startDateExists = startDate !== undefined && startDate.length > 0;
    const endDateExists = endDate !== undefined && endDate.length > 0;

    if ((startDateExists && !endDateExists) || (!startDateExists && endDateExists)) {
      const windowName = this.getWindowName(dateTypeCode);
      this.setValidationMessage(
        dateTypeCode,
        `Both start date and end date must be provided for ${windowName}`
      );
      return false;
    }

    let isValid = true;

    if (startDate && endDate && startDate >= endDate) {
      this.setValidationMessage(
        dateTypeCode,
        'Start date must be earlier than end date.',
      );
      isValid = false;
    }

    if (displayStartDate && displayEndDate && displayStartDate >= displayEndDate) {
      this.setValidationMessage(
        dateTypeCode,
        'Display start date must be earlier than display end date.',
      );
      isValid = false;
    }

    if (isValid) {
      this.clearValidationMessage(dateTypeCode);
    }

    return isValid;
  }

  private validatePreviousProgramDates(programDates: ProgramDate[], currentProgramDate: ProgramDate): boolean {
    const currentDateTypeValue = ProgramDateTypes[currentProgramDate.dateTypeCode];
    
    if (currentDateTypeValue <= 1 || (!currentProgramDate.startDate && !currentProgramDate.endDate)) {
      return true;
    }
    
    let isValid = true;
    const previousProgramDates = programDates.filter(
      date => ProgramDateTypes[date.dateTypeCode] < currentDateTypeValue
    );
    
    for (const prevDate of previousProgramDates) {
      const startDateEmpty = !prevDate.startDate || prevDate.startDate.length === 0;
      const endDateEmpty = !prevDate.endDate || prevDate.endDate.length === 0;
      
      if (startDateEmpty || endDateEmpty) {
        if (!this.validationMessages.get(prevDate.dateTypeCode)) {
          const prevWindowName = this.getWindowName(prevDate.dateTypeCode);
          this.setValidationMessage(
            prevDate.dateTypeCode,
            `Enter a start date and an end date for ${prevWindowName}.`
          );
        }
        
        if (!this.validationMessages.get(currentProgramDate.dateTypeCode)) {
          const currentWindowName = this.getWindowName(currentProgramDate.dateTypeCode);
          const prevWindowName = this.getWindowName(prevDate.dateTypeCode);
          this.setValidationMessage(
            currentProgramDate.dateTypeCode,
            `Complete ${prevWindowName} before you set dates for ${currentWindowName}.`
          );
        }
        
        isValid = false;
      }
    }
    
    return isValid;
  }

  private validateDateOverlaps(programDates: ProgramDate[]): boolean {
    let isValid = true;
    const datesWithValues = programDates.filter(
      date => date.startDate && date.endDate
    );

    for (let i = 0; i < datesWithValues.length; i++) {
      for (let j = i + 1; j < datesWithValues.length; j++) {
        const date1 = datesWithValues[i];
        const date2 = datesWithValues[j];

        if (this.canWindowsOverlap(date1.dateTypeCode, date2.dateTypeCode)) {
          continue;
        }

        if (this.doDateRangesOverlap(
          date1.startDate,
          date1.endDate,
          date2.startDate,
          date2.endDate
        )) {
          const window1Name = this.getWindowName(date1.dateTypeCode);
          const window2Name = this.getWindowName(date2.dateTypeCode);
          
          this.setValidationMessage(
            date1.dateTypeCode,
            `${window1Name} dates cannot overlap with ${window2Name}`
          );
          this.setValidationMessage(
            date2.dateTypeCode,
            `${window2Name} dates cannot overlap with ${window1Name}`
          );
          
          isValid = false;
        }
      }
    }

    return isValid;
  }

  @action
  setValidationMessage(dateTypeCode: ProgramDateTypeCode, message: string): void {
    this.validationMessages.set(dateTypeCode, message);
  }

  @action
  clearValidationMessage(dateTypeCode: ProgramDateTypeCode): void {
    this.validationMessages.set(dateTypeCode, '');
  }

  @action
  clearValidationMessages(): void {
    this.validationMessages.clear();
  }

  @action
  validateAllProgramDates(): boolean {
    let isValid = true;

    this.clearValidationMessages();

    const sortedProgramDates = [...this.editingProgramDates].sort(
      (a, b) => ProgramDateTypes[a.dateTypeCode] - ProgramDateTypes[b.dateTypeCode]
    );
    
    for (const programDate of this.editingProgramDates) {
      const hasOperationalDates = !!programDate.startDate || !!programDate.endDate;
      const hasDisplayDates = !!programDate.displayStartDate || !!programDate.displayEndDate;

      if (hasOperationalDates || hasDisplayDates) {
        const isProgramDateValid = this.validateProgramDate(programDate);
        if (!isProgramDateValid) {
          isValid = false;
        }

        if (!hasOperationalDates) continue;

        const arePreviousDatesValid = this.validatePreviousProgramDates(
          sortedProgramDates, 
          programDate
        );
        
        if (!arePreviousDatesValid) {
          isValid = false;
        }
      }
    }

    const areOverlapsValid = this.validateDateOverlaps(this.editingProgramDates);
    if (!areOverlapsValid) {
      isValid = false;
    }

    return isValid;
  }

}
